import { BaseWebComponentView } from '../../../../../framework/BaseWebComponentView';
import { GzToastNotificationWebComponent } from '../../../../../ui/gzToastNotification';
import { ToastSeverity } from '../../../../../ui/gzToastNotification/type';
import { toggleVisibility } from '../../../../../utils/htmlElement.utils';
import { TemplateState } from '../../../../../types/common/types';
import { translateFn } from '../../../../../utils/translator.helper';
import { GzRadioGroupWebComponent, GzRadioWebComponent } from '../../../../../ui/gzRadio';
import { FormGroup } from '../../../../../ui/gzForm/formGroup/form-group';
import { GzSingleSelectComplexWebComponent } from '../../../../../ui/gzSingleSelectComplex';
import { GzFormFieldWebComponent } from '../../../../../ui/gzForm/formField';
import {
  API_TYPE_ACCOUNTS_FOR_MDR_CONTACT,
  API_TYPE_COMPANIES_FOR_MDR_CONTACT,
} from '../../../../../webcomponents.constants';
import { GetItemsResponse } from '../../../../../ui/gzSingleSelectComplex/types';
import { RequiredValidator } from '../../../../../ui/gzForm/validators/required.validator';
import { AccountsForMdrContactDataProvider } from '../../../../../ui/gzSingleSelectComplex/dataProvider/company/AccountsForMdrContactDataProvider';
import { CompaniesForMdrContactDataProvider } from '../../../../../ui/gzSingleSelectComplex/dataProvider/company/CompaniesForMdrContactDataProvider';
import { ImportOptionsViewData, ImportOptionsLayout } from '../types/ImportOptionsViewData';
import { GzHardCodedFormField } from '../../../../../ui/gzForm/formField/component/GzHardCodedFormField';
import { MdrContactDetailsSource } from '../../common/types';
import { DEFAULT_PAGE_SIZE } from '../../../../../accounts/accountsGrid/types';
import { MAX_COMPANY_LIMIT } from '../../../../../ui/gzCompanyFilter/types/types';

export class ImportOptionsView extends BaseWebComponentView {
  public formGroup!: FormGroup;
  public storedFormGroup!: FormGroup; // Used for storing the imported options that are currently used in MdrContactDetailsForm

  public dom: {
    $wrapper: HTMLElement;
    $radioGroup: GzFormFieldWebComponent<GzRadioGroupWebComponent, string | number>;
    $radioPartner: GzRadioWebComponent;
    $radioPartnerAccount: GzRadioWebComponent;
    $partnerSelector: GzFormFieldWebComponent<GzSingleSelectComplexWebComponent, string | number>;
    $accountSelector: GzFormFieldWebComponent<GzSingleSelectComplexWebComponent, string | number>;
    $accountSelectorFromSelf: GzFormFieldWebComponent<GzSingleSelectComplexWebComponent, string | number>;
    $dropdownContainers: HTMLElement[];
    $partnerContactContainer: HTMLElement;
  };

  initialSetup() {
    super.initialSetup();

    this.formGroup = new FormGroup({});
    this.storedFormGroup = new FormGroup({
      importedCompanyId: new GzHardCodedFormField<string | null>(null),
      importedAccountId: new GzHardCodedFormField<string | null>(null),
    });
  }

  getDomElements() {
    this.dom = {
      $wrapper: this.shadow.querySelector('.wrapper'),
      $radioGroup: this.shadow.querySelector('#import-options-multi-tenancy'),
      $radioPartner: this.shadow.querySelector('#partner-contact'),
      $radioPartnerAccount: this.shadow.querySelector('#partner-account'),
      $partnerSelector: this.shadow.querySelector('#select-partner-form-field'),
      $accountSelector: this.shadow.querySelector('#select-account-form-field'),
      $accountSelectorFromSelf: this.shadow.querySelector('#self-form-field'),
      $dropdownContainers: this.shadow.querySelectorAll('.dropdown-container'),
      $partnerContactContainer: this.shadow.querySelector('#partner-contact-container'),
    };

    this.setTranslatedStrings();
  }

  setTranslatedStrings() {
    // Multi tenancy section
    this.dom.$radioPartner.label = translateFn('Use data from a company');
    this.dom.$radioPartnerAccount.label = translateFn('Use data from a user account');

    if (this.dom.$partnerSelector.Input) {
      this.dom.$partnerSelector.Input.placeholder = this.dependencies.translateFn('Select a company contact');
      this.dom.$partnerSelector.Input.searchPlaceholderString = this.dependencies.translateFn('Search');
      this.dom.$partnerSelector.Input.noSearchResultsString = this.dependencies.translateFn('No options found');
    }

    if (this.dom.$accountSelector.Input) {
      this.dom.$accountSelector.Input.placeholder = this.dependencies.translateFn('Select a user account');
      this.dom.$accountSelector.Input.searchPlaceholderString = this.dependencies.translateFn('Search');
      this.dom.$accountSelector.Input.noSearchResultsString = this.dependencies.translateFn('No options found');
    }

    this.dom.$accountSelectorFromSelf.label = this.dependencies.translateFn('Use data from a user account');
    if (this.dom.$accountSelectorFromSelf.Input) {
      this.dom.$accountSelectorFromSelf.Input.placeholder = this.dependencies.translateFn('Select a user account');
      this.dom.$accountSelectorFromSelf.Input.searchPlaceholderString = this.dependencies.translateFn('Search');
      this.dom.$accountSelectorFromSelf.Input.noSearchResultsString = this.dependencies.translateFn('No options found');
    }
  }

  buildSuccessView(viewData: ImportOptionsViewData) {
    const layout = viewData.layout as ImportOptionsLayout;

    this.dom.$radioGroup.Validators = [RequiredValidator('')];
    this.setStackedOptions(viewData?.hasStackedFormFields ?? false);

    switch (layout) {
      case ImportOptionsLayout.SELF_COMPANY_RADIO_GROUP:
        this.setupSelfCompanyRadioGroupLayout(viewData);
        break;
      case ImportOptionsLayout.ANY_COMPANY_RADIO_GROUP:
        this.setupAnyCompanyRadioGroupLayout(viewData);
        break;
      case ImportOptionsLayout.SELF_ACCOUNT_ONLY:
        this.setupSelfAccountOnlyLayout(viewData);
        break;
    }
  }

  private setupSelfCompanyRadioGroupLayout(viewData: ImportOptionsViewData): void {
    this.dom.$radioPartner.label = translateFn('Use data from my company');
    this.buildAccountSelector(this.dom.$accountSelector, viewData.editedCompanyId).then();

    toggleVisibility(this.dom.$partnerSelector, false);
    toggleVisibility(this.dom.$accountSelectorFromSelf, false);
    toggleVisibility(this.dom.$radioGroup, true);

    this.dom.$accountSelectorFromSelf.disabled = true;
    this.dom.$radioGroup.disabled = false;

    this.formGroup.addFormChild(['importSource'], this.dom.$radioGroup);
  }

  private setupAnyCompanyRadioGroupLayout(viewData: ImportOptionsViewData): void {
    this.dom.$radioPartner.label = translateFn('Use data from a company');
    this.buildCompanySelector(this.dom.$partnerSelector, viewData.editedCompanyId).then();
    this.buildAccountSelector(this.dom.$accountSelector, viewData.editedCompanyId).then();

    toggleVisibility(this.dom.$accountSelectorFromSelf, false);
    toggleVisibility(this.dom.$partnerSelector, true);
    toggleVisibility(this.dom.$radioGroup, true);
    toggleVisibility(this.dom.$partnerContactContainer, !viewData.shouldHidePartnerImport);

    this.dom.$accountSelectorFromSelf.disabled = true;
    this.dom.$radioGroup.disabled = false;

    this.formGroup.addFormChild(['importSource'], this.dom.$radioGroup);
  }

  private setupSelfAccountOnlyLayout(viewData: ImportOptionsViewData): void {
    this.buildAccountSelector(this.dom.$accountSelectorFromSelf, viewData.editedCompanyId).then();

    toggleVisibility(this.dom.$accountSelectorFromSelf, true);
    toggleVisibility(this.dom.$radioGroup, false);

    this.dom.$accountSelectorFromSelf.disabled = false;
    this.dom.$radioGroup.disabled = true;

    this.formGroup.addFormChild(['importSource'], new GzHardCodedFormField(String(MdrContactDetailsSource.GZ_ACCOUNT)));
    this.formGroup.addFormChild(['importAccount', 'accountId'], this.dom.$accountSelectorFromSelf);
  }

  buildErrorView(error: Error) {
    this.removeLoadingState();
    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: error.message,
      severity: ToastSeverity.ERROR,
    });
    toggleVisibility(this.dom.$wrapper, false);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  protected onDisconnect() {
    this.formGroup.unsubscribe();
    this.storedFormGroup.unsubscribe();
  }

  private async buildCompanySelector(
    $selector: GzFormFieldWebComponent<GzSingleSelectComplexWebComponent, string | number>,
    companyId: string | undefined
  ) {
    $selector.disabled = true;
    $selector.Input.pageLimit = MAX_COMPANY_LIMIT;
    $selector.Input.setApiType(API_TYPE_COMPANIES_FOR_MDR_CONTACT);

    const dataProvider = $selector.Input.controller.dataRepository.dataProvider as CompaniesForMdrContactDataProvider;
    dataProvider.editedCompanyId = companyId;

    const companies: GetItemsResponse = {
      items: [],
      totalItemsCount: 0,
    };

    $selector.Input.updateComplete.then(() => {
      $selector.Input.controller.setFilterOptions(companies).catch();
    });
  }

  private async buildAccountSelector(
    $selector: GzFormFieldWebComponent<GzSingleSelectComplexWebComponent, string | number>,
    companyId: string | undefined
  ) {
    $selector.disabled = true;
    $selector.Input.pageLimit = DEFAULT_PAGE_SIZE;
    $selector.Input.setApiType(API_TYPE_ACCOUNTS_FOR_MDR_CONTACT);

    const dataProvider = $selector.Input.controller.dataRepository.dataProvider as AccountsForMdrContactDataProvider;
    dataProvider.editedCompanyId = companyId;

    const accountsData: GetItemsResponse = {
      items: [],
      totalItemsCount: 0,
    };

    $selector.Input.updateComplete.then(() => {
      $selector.Input.controller.setFilterOptions(accountsData).catch();
    });
  }

  private setStackedOptions(isStacked: boolean): void {
    this.dom.$dropdownContainers.forEach((container: HTMLElement) => {
      if (isStacked) {
        container.setAttribute('stacked', '');
      } else {
        container.removeAttribute('stacked');
      }
    });
    this.dom.$accountSelectorFromSelf.stacked = isStacked;
    this.dom.$partnerSelector.disableVerticalMargin = isStacked;
    this.dom.$accountSelector.disableVerticalMargin = isStacked;
    this.dom.$accountSelectorFromSelf.disableVerticalMargin = isStacked;
  }
}
